import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { config } from '../config';
import '../styles/tailwind.css';

// Declare DashzapForms type for TypeScript
declare global {
  interface Window {
    DashzapForms: any;
  }
}

const MigrationServices: React.FC = () => {
  const navigate = useNavigate();
  const [userCount, setUserCount] = useState(10);

  // Load Dashzap Forms SDK and initialize
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `${config.FRONTEND_BASE_URL}/dashzap-forms.js`;
    script.async = true;
    script.onload = () => {
      // Initialize the form handler once SDK is loaded
      if (window.DashzapForms) {
        new window.DashzapForms({
          formId: 'email-migration-inquiry-form',
          companyId: 'dashzapcom',
          callbacks: {
            onSuccess: function(data: any) {
              const userCounts: { [key: string]: number } = {
                '1-5': 3,
                '6-10': 8,
                '11-25': 18,
                '26-50': 38,
                '50+': 75
              };

              const estimatedUsers = userCounts[data.users] || 10;
              const estimatedMonthlySavings = estimatedUsers * 425;
              const estimatedAnnualSavings = estimatedMonthlySavings * 12;
              const estimatedFee = estimatedMonthlySavings * 3;
              const estimatedNetSavings = estimatedAnnualSavings - estimatedFee;

              alert(`Thank you ${data.name}! We'll send your personalized quote to ${data.email}.

💰 Your Estimated Savings:
• Annual Savings: ₹${estimatedAnnualSavings.toLocaleString()}
• Our Fee: ₹${estimatedFee.toLocaleString()}
• Your Net Savings: ₹${estimatedNetSavings.toLocaleString()}

We'll respond within 24 hours.`);
            },
            onError: function(error: any) {
              alert('Failed to send message. Please try again or contact us at support@dashzap.com');
            }
          }
        });
      }
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Calculate savings
  const monthlySavingsPerUser = 425; // ₹500 - ₹75
  const monthlySavings = userCount * monthlySavingsPerUser;
  const annualSavings = monthlySavings * 12;
  const ourFee = monthlySavings * 3; // 3 months of savings
  const netSavings = annualSavings - ourFee;


  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-indigo-600 cursor-pointer" onClick={() => navigate('/')}>
            Dashzap
          </h1>
          <Button variant="outline" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Cut Your Email Costs by <span className="text-yellow-500">85%</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-6">
              Migrate from 🇺🇸 Google Workspace to 🇮🇳 Zoho Mail with zero downtime.
              Save money while supporting indigenous proven email technology.
            </p>
            <div className="inline-block bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-full font-bold text-xl mb-4">
              Pay only 3 months of savings - Keep 9+ months profit
            </div>
            <p className="text-lg text-gray-600 mb-8">
              🇺🇸 ₹500/user/month → 🇮🇳 ₹75/user/month
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-6 text-lg"
                onClick={() => document.getElementById('inquiry-form')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get Your Savings Quote
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 px-8 py-6 text-lg"
                onClick={() => document.getElementById('case-study')?.scrollIntoView({ behavior: 'smooth' })}
              >
                See Success Story
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Migrate to Zoho Mail?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="text-5xl mb-4">💰</div>
                <h3 className="text-xl font-bold mb-2">85% Cost Reduction</h3>
                <p className="text-gray-600">Save thousands annually with same email functionality.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="text-5xl mb-4">🇮🇳</div>
                <h3 className="text-xl font-bold mb-2">Indian Technology</h3>
                <p className="text-gray-600">Chennai-based innovation with local support.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-yellow-500 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="text-5xl mb-4">🛡️</div>
                <h3 className="text-xl font-bold mb-2">Risk-Free</h3>
                <p className="text-gray-600">Test Zoho alongside Gmail before switching.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-indigo-500 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="text-5xl mb-4">💻</div>
                <h3 className="text-xl font-bold mb-2">Desktop Compatible</h3>
                <p className="text-gray-600">Works with Outlook, Thunderbird, Apple Mail.</p>
              </CardContent>
            </Card>
          </div>

          {/* Storage Disclaimer */}
          <div className="mt-12 max-w-4xl mx-auto">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <h3 className="text-lg font-bold mb-3">Storage Comparison</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Google Workspace:</strong> ₹500/month per user (30 GB storage)<br />
                  <strong>Zoho Mail:</strong> ₹75/month per user (10 GB storage)
                </p>
                <p className="text-sm text-gray-600 italic">
                  Note: For users accessing email primarily through desktop clients (Outlook, Thunderbird, Apple Mail),
                  10 GB cloud storage is typically sufficient as emails are stored locally on your device.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section id="case-study" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-l-4 border-l-green-500">
              <CardHeader>
                <div className="text-center">
                  <CardTitle className="text-3xl mb-4">Success Story: Ahmedabad SME Company</CardTitle>
                  <div className="inline-block bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-full font-bold text-lg mb-2">
                    Successfully migrated with 75% cost reduction
                  </div>
                  <p className="text-sm text-gray-600">100-employee company, 20 Google Workspace users</p>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-bold text-blue-600 mb-3">Challenge:</h3>
                    <p className="text-gray-700 mb-6">
                      This Ahmedabad-based SME was paying premium rates for Google Workspace across all 20 email accounts,
                      resulting in high email infrastructure costs of ₹10,000/month.
                    </p>

                    <h3 className="text-xl font-bold text-blue-600 mb-3">Solution:</h3>
                    <p className="text-gray-700">
                      Implemented hybrid email setup with selective migration to Zoho Mail for standard accounts
                      while maintaining premium Gmail for critical functions.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-green-600 mb-3">Results:</h3>
                    <ul className="space-y-2 text-gray-700 mb-6">
                      <li>✅ <strong>75% cost reduction</strong> (₹10,000 → ₹2,500/month)</li>
                      <li>✅ <strong>Zero downtime</strong> during transition</li>
                      <li>✅ <strong>100% user satisfaction</strong> with new setup</li>
                      <li>✅ <strong>Seamless desktop client</strong> integration</li>
                      <li>✅ <strong>Option to revert</strong> maintained as fallback</li>
                    </ul>

                    <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600">
                      "The migration was seamless and the cost savings are substantial.
                      Our team loves the new setup and we haven't looked back."
                    </blockquote>

                    <p className="mt-4 text-sm text-gray-500">
                      📞 Contact details available upon request for reference
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Fair Pricing</h2>
            <p className="text-xl text-gray-600">Pay just 3 months of your savings - guaranteed 9+ months of pure profit</p>
          </div>

          <div className="max-w-5xl mx-auto">
            <Card>
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-blue-600 mb-2">Our Pricing Model</h3>
                  <p className="text-lg text-gray-600">We only charge 3 months of your actual savings</p>
                </div>

                {/* 3 Step Process */}
                <div className="grid md:grid-cols-3 gap-6 mb-12">
                  <Card className="bg-gray-50">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl mb-4">🧮</div>
                      <h4 className="text-lg font-bold text-blue-600 mb-2">Calculate Savings</h4>
                      <p className="text-gray-700">Analyze Google Workspace vs Zoho Mail costs</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-50">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl mb-4">📅</div>
                      <h4 className="text-lg font-bold text-blue-600 mb-2">3 Months Fee</h4>
                      <p className="text-gray-700">Our fee = 3 months of your savings</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-50">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl mb-4">🏆</div>
                      <h4 className="text-lg font-bold text-blue-600 mb-2">9+ Months Profit</h4>
                      <p className="text-gray-700">Rest of the year = pure savings</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Interactive Calculator */}
                <Card className="bg-gradient-to-br from-blue-600 to-indigo-600">
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold text-white text-center mb-6">Savings Calculator</h3>
                    <Card>
                      <CardContent className="p-6">
                        <div className="mb-6">
                          <Label htmlFor="userCount" className="text-lg font-bold mb-3 block">
                            Number of Email Users:
                          </Label>
                          <Input
                            id="userCount"
                            type="number"
                            min="1"
                            max="1000"
                            value={userCount}
                            onChange={(e) => setUserCount(parseInt(e.target.value) || 0)}
                            className="text-center text-2xl font-bold border-2 border-blue-500"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <Card className="bg-gray-50">
                            <CardContent className="pt-4 text-center">
                              <p className="text-sm text-gray-600 mb-1">Annual Savings</p>
                              <p className="text-2xl font-bold text-green-600">₹{annualSavings.toLocaleString()}</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-gray-50">
                            <CardContent className="pt-4 text-center">
                              <p className="text-sm text-gray-600 mb-1">Our Fee (3 months)</p>
                              <p className="text-2xl font-bold text-blue-600">₹{ourFee.toLocaleString()}</p>
                            </CardContent>
                          </Card>
                        </div>

                        <Card className="bg-green-500 text-white">
                          <CardContent className="pt-4 text-center">
                            <p className="text-sm mb-1">Your Net Savings</p>
                            <p className="text-3xl font-bold">₹{netSavings.toLocaleString()}</p>
                          </CardContent>
                        </Card>
                      </CardContent>
                    </Card>
                  </CardContent>
                </Card>

                {/* Guarantee */}
                <div className="text-center mt-6">
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-4">
                      <p className="text-green-800">
                        🛡️ <strong>Guaranteed:</strong> Annual contracts ensure 12 months of savings
                      </p>
                    </CardContent>
                  </Card>
                  <p className="text-gray-600 mt-4">
                    <strong>No upfront cost</strong> - We calculate your exact savings first
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Inquiry Form Section */}
      <section id="inquiry-form" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-blue-900 to-indigo-900 text-white">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-3">Ready to Save 85% on Email Costs?</h2>
                <p className="text-xl">Get your personalized migration quote and timeline</p>
              </div>

              <form id="email-migration-inquiry-form" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name*"
                      required
                      className="bg-white text-gray-900"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Business Email*"
                      required
                      className="bg-white text-gray-900"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      type="text"
                      name="company"
                      placeholder="Company Name*"
                      required
                      className="bg-white text-gray-900"
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      className="bg-white text-gray-900"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <select
                      name="users"
                      required
                      className="w-full px-3 py-2 rounded-md border bg-white text-gray-900"
                    >
                      <option value="">Number of Email Users*</option>
                      <option value="1-5">1-5 users</option>
                      <option value="6-10">6-10 users</option>
                      <option value="11-25">11-25 users</option>
                      <option value="26-50">26-50 users</option>
                      <option value="50+">50+ users</option>
                    </select>
                  </div>
                  <div>
                    <select
                      name="currentProvider"
                      className="w-full px-3 py-2 rounded-md border bg-white text-gray-900"
                    >
                      <option value="">Current Email Provider</option>
                      <option value="google-workspace">🇺🇸 Google Workspace</option>
                      <option value="microsoft-365">Microsoft 365</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <select
                      name="timeline"
                      className="w-full px-3 py-2 rounded-md border bg-white text-gray-900"
                    >
                      <option value="">Preferred Timeline</option>
                      <option value="asap">ASAP (Within 2 weeks)</option>
                      <option value="month">Within a month</option>
                      <option value="quarter">Within 3 months</option>
                      <option value="planning">Just planning ahead</option>
                    </select>
                  </div>
                  <div>
                    <select
                      name="desktopClient"
                      className="w-full px-3 py-2 rounded-md border bg-white text-gray-900"
                    >
                      <option value="">Desktop Email Client</option>
                      <option value="outlook">Microsoft Outlook</option>
                      <option value="thunderbird">Mozilla Thunderbird</option>
                      <option value="apple-mail">Apple Mail</option>
                      <option value="other">Other</option>
                      <option value="none">Web-only</option>
                    </select>
                  </div>
                </div>

                <div>
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="Tell us about your current setup, specific requirements, or concerns about migration..."
                    className="w-full px-3 py-2 rounded-md border bg-white text-gray-900"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="newsletter"
                    name="newsletter"
                    className="rounded"
                  />
                  <label htmlFor="newsletter" className="text-sm cursor-pointer">
                    I'd like to receive updates about cost optimization tips and migration best practices
                  </label>
                </div>

                <div className="text-center">
                  <Button
                    type="submit"
                    size="lg"
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-6 text-lg"
                  >
                    📧 Get My Savings Quote
                  </Button>
                </div>

                <div className="text-center">
                  <p className="text-sm opacity-75">
                    We'll respond within 24 hours with your personalized migration plan and cost analysis
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Will there be any downtime during migration?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  <strong>Zero downtime guaranteed.</strong> We use a hybrid setup approach where both systems run
                  simultaneously during testing phase. Your team can continue using Gmail while we set up and test
                  Zoho in parallel.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What if Zoho Mail doesn't work well for our team?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  Our hybrid approach allows you to test Zoho Mail alongside Gmail before making the full switch.
                  If you're not satisfied, you can easily revert to Gmail without any data loss or service interruption.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Will my desktop email client (Outlook/Thunderbird) work?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  Absolutely! Zoho Mail fully supports POP/IMAP protocols. We'll reconfigure your existing desktop
                  email clients to work seamlessly with Zoho Mail servers.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>How long does the migration process take?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  The complete migration typically takes 1-2 weeks: setup and testing (3-5 days), user training (2-3 days),
                  and data migration (1-2 days). Timeline may vary based on organization size and complexity.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What about our existing emails and data?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  We use Zoho's official migration tools to transfer all existing emails, contacts, and calendars from Gmail.
                  Your historical data remains intact and searchable in Zoho Mail.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Start Saving Today</h2>
          <p className="text-xl mb-8">
            Join the growing number of Indian businesses choosing cost-effective, India-first email solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-6 text-lg"
              onClick={() => document.getElementById('inquiry-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Get Your Quote
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
              onClick={() => navigate('/')}
            >
              Learn More About Dashzap
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="mb-4 md:mb-0">&copy; 2024 Dashzap. All rights reserved.</p>
            <div className="flex gap-6">
              <button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">
                Home
              </button>
              <button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">
                Services
              </button>
              <button onClick={() => navigate('/')} className="hover:text-indigo-400 transition-colors">
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MigrationServices;
